import { Button, ButtonVariant } from '@Core/Components'
import { FilterSelect } from '@/components/filter-select'
import { Input } from '@/components/ui/input'
import { ExistingProcessMapping } from '@Purchasing/Domain/Model/PurchaseProcess'
import { formatBRL } from '@Purchasing/Utils/money'
import { DraftGroup, OrganizerItem } from './organizer.types'

// ---------------------------------------------------------------------------
// Shared design tokens — Fase 2.2.1 UX polish
// Ferramenta operacional: densidade moderada, hierarquia por tipografia,
// borda e espaçamento (não por cor). Dark mode via classes dark:* existentes.
// ---------------------------------------------------------------------------

export const inputClass =
    'w-full'

export const miniSelectClass =
    'h-8 text-xs'

export const ghostButtonClass =
    'rounded-full px-2 py-0.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-od-secondary-50 hover:text-od-secondary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'

export const chipClass =
    'inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground'

export const cardClass =
    'rounded-lg border border-border bg-card p-4  '

export const isSuggestedGroup = (temporaryId: string) =>
    /^(PRODUCT|SKU|NAME|ITEM):/.test(temporaryId)

const processWord = (n: number) => (n === 1 ? 'processo' : 'processos')

export const createProcessesLabel = (n: number) =>
    n === 1 ? 'Criar processo' : `Criar ${n} processos`

// ---------------------------------------------------------------------------
// BudgetSourceItemRow — linha compacta de um item do orçamento (§11–§14)
// ---------------------------------------------------------------------------

interface SourceItemRowProps {
    item: OrganizerItem
    bound?: ExistingProcessMapping
    moveOptions?: Array<{ value: string; label: string }>
    onMove?: (itemId: string, target: string) => void
    onRemove?: (itemId: string) => void
    removeLabel?: string
    moveAriaLabel?: string
}

export const BudgetSourceItemRow = ({
    item,
    bound,
    moveOptions,
    onMove,
    onRemove,
    removeLabel = 'Retirar do grupo',
    moveAriaLabel = 'Mover item',
}: SourceItemRowProps) => {
    const price =
        item.unitPrice != null && item.unitPrice > 0
            ? ` • ${formatBRL(item.unitPrice)}`
            : ''

    return (
        <li
            className="flex items-center justify-between gap-3 py-1.5"
            data-testid={`organizer-item-${item.sourceItemExternalId}`}
        >
            <div className="min-w-0">
                <p
                    className="truncate text-sm text-foreground line-clamp-2"
                    title={item.description ?? undefined}
                >
                    {item.description || '(sem descrição)'}
                </p>
                <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                    {item.sku ? `${item.sku} • ` : ''}
                    {item.quantity} un.{price}
                </p>
            </div>

            {bound ? (
                <span
                    className="inline-flex shrink-0 items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground"
                    data-testid={`bound-badge-${item.sourceItemExternalId}`}
                >
                    Já vinculado ao {bound.processCode}
                </span>
            ) : (
                <div className="flex shrink-0 items-center gap-1">
                    {moveOptions && moveOptions.length > 0 && (
                        <FilterSelect
                            label={moveAriaLabel}
                            placeholder="Mover…"
                            value=""
                            onChange={(value) => {
                                if (value && onMove) onMove(item.sourceItemExternalId, value)
                            }}
                            options={moveOptions}
                            className="h-8 min-w-[120px] text-xs"
                        />
                    )}
                    {onRemove && (
                        <button
                            type="button"
                            onClick={() => onRemove(item.sourceItemExternalId)}
                            className={ghostButtonClass}
                            data-testid={`remove-item-${item.sourceItemExternalId}`}
                        >
                            {removeLabel}
                        </button>
                    )}
                </div>
            )}
        </li>
    )
}

// ---------------------------------------------------------------------------
// PurchaseDraftGroupCard — card compacto de um grupo/processo (§9–§10, §17)
// ---------------------------------------------------------------------------

interface DraftGroupCardProps {
    index: number
    group: DraftGroup
    suggested: boolean
    boundByItemId: Map<string, ExistingProcessMapping>
    otherGroups: Array<{ temporaryId: string; title: string }>
    onTitleChange: (title: string) => void
    onQuantityChange: (quantity: number | null) => void
    onMoveItem: (itemId: string, target: string) => void
    onRemoveItem: (itemId: string) => void
    onDeleteGroup: () => void
}

export const PurchaseDraftGroupCard = ({
    index,
    group,
    suggested,
    boundByItemId,
    otherGroups,
    onTitleChange,
    onQuantityChange,
    onMoveItem,
    onRemoveItem,
    onDeleteGroup,
}: DraftGroupCardProps) => {
    const moveOptions = [
        { value: 'new', label: '+ Novo processo' },
        ...otherGroups.map((g) => ({
            value: g.temporaryId,
            label: g.title || '(sem título)',
        })),
    ]

    return (
        <div
            className={cardClass}
            data-testid={`group-card-${group.temporaryId}`}
        >
            {/* Cabeçalho */}
            <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground dark:text-muted-foreground">
                        Processo {index}
                    </span>
                    {suggested && (
                        <span
                            className={chipClass}
                            title="Agrupamento sugerido automaticamente pelo sistema"
                        >
                            Sugestão automática
                        </span>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <span className={chipClass}>
                        {group.items.length}{' '}
                        {group.items.length === 1 ? 'item' : 'itens'}
                    </span>
                    <button
                        type="button"
                        onClick={onDeleteGroup}
                        className={ghostButtonClass}
                        title="Os itens voltarão para “Não organizados”."
                        aria-label={`Remover grupo ${group.title || index}`}
                    >
                        Remover grupo
                    </button>
                </div>
            </div>

            {/* Nome + quantidade integrados ao card */}
            <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="min-w-0 flex-1">
                    <span className="mb-0.5 block text-[11px] text-muted-foreground dark:text-muted-foreground">
                        Nome do processo
                    </span>
                    <Input
                        value={group.title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        placeholder="Título da necessidade"
                        className={inputClass}
                        aria-label="Título do processo"
                    />
                </label>
                <label className="w-full sm:w-28">
                    <span className="mb-0.5 block text-[11px] text-muted-foreground dark:text-muted-foreground">
                        Quantidade
                    </span>
                    <Input
                        type="number"
                        min={1}
                        value={group.quantity ?? ''}
                        onChange={(e) =>
                            onQuantityChange(
                                e.target.value === ''
                                    ? null
                                    : Number(e.target.value)
                            )
                        }
                        placeholder="—"
                        className={inputClass}
                        aria-label="Quantidade da necessidade"
                    />
                </label>
            </div>

            {group.quantity == null && group.items.length > 0 && (
                <p className="mb-1 text-xs text-warning-500" role="note">
                    Informe a quantidade da necessidade — não somamos as linhas
                    automaticamente.
                </p>
            )}

            {/* Itens */}
            <ul className="divide-y divide-border">
                {group.items.map((item) => (
                    <BudgetSourceItemRow
                        key={item.sourceItemExternalId}
                        item={item}
                        bound={boundByItemId.get(item.sourceItemExternalId)}
                        moveOptions={
                            boundByItemId.get(item.sourceItemExternalId)
                                ? undefined
                                : moveOptions
                        }
                        onMove={(itemId, target) => onMoveItem(itemId, target)}
                        onRemove={onRemoveItem}
                    />
                ))}
                {group.items.length === 0 && (
                    <li className="py-2 text-xs italic text-muted-foreground">
                        Grupo vazio — será ignorado na criação.
                    </li>
                )}
            </ul>
        </div>
    )
}

// ---------------------------------------------------------------------------
// UnassignedBudgetItems — itens fora dos grupos (§18)
// ---------------------------------------------------------------------------

interface UnassignedItemsProps {
    items: OrganizerItem[]
    groups: Array<{ temporaryId: string; title: string }>
    onMoveItem: (itemId: string, target: string) => void
}

export const UnassignedBudgetItems = ({
    items,
    groups,
    onMoveItem,
}: UnassignedItemsProps) => {
    if (items.length === 0) return null

    const moveOptions = [
        { value: 'new', label: '+ Novo processo' },
        ...groups.map((g) => ({
            value: g.temporaryId,
            label: g.title || '(sem título)',
        })),
    ]

    return (
        <div
            className={`${cardClass} border-warning-200 bg-warning-50/60  `}
            data-testid="unassigned-items-card"
        >
            <div className="mb-1 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                    Não organizados
                </h3>
                <span className={chipClass}>
                    {items.length} {items.length === 1 ? 'item' : 'itens'}
                </span>
            </div>
            <p className="mb-2 text-xs text-muted-foreground dark:text-muted-foreground">
                Estes itens não serão incluídos em nenhum processo.
            </p>
            <ul className="divide-y divide-border">
                {items.map((item) => (
                    <div
                        key={item.sourceItemExternalId}
                        data-testid={`ungrouped-item-${item.sourceItemExternalId}`}
                    >
                        <BudgetSourceItemRow
                            item={item}
                            moveOptions={moveOptions}
                            onMove={onMoveItem}
                            removeLabel="Manter fora"
                            moveAriaLabel="Adicionar ao grupo"
                        />
                    </div>
                ))}
            </ul>
        </div>
    )
}

// ---------------------------------------------------------------------------
// PurchaseBatchSummary — sidebar/resumo (§19–§22)
// ---------------------------------------------------------------------------

interface BatchSummaryProps {
    activeGroups: DraftGroup[]
    totalBudgetItems: number
    unassignedCount: number
    creating?: boolean
    pendingExcludedConfirm: boolean
    onCreateClick: () => void
    onCancelExcluded: () => void
    onConfirmExcluded: () => void
}

export const PurchaseBatchSummary = ({
    activeGroups,
    totalBudgetItems,
    unassignedCount,
    creating,
    pendingExcludedConfirm,
    onCreateClick,
    onCancelExcluded,
    onConfirmExcluded,
}: BatchSummaryProps) => {
    const includedCount = activeGroups.reduce(
        (acc, g) => acc + g.items.length,
        0
    )
    // Todo item do orçamento está em um grupo ou na lista de não organizados;
    // portanto o total fora = não organizados.
    const outCount = unassignedCount
    const canSubmit =
        activeGroups.length > 0 &&
        activeGroups.every(
            (g) => g.title.trim().length > 0 && (g.quantity ?? 0) > 0
        )

    return (
        <div className={`${cardClass} space-y-4`} data-testid="batch-preview">
            <div>
                <h3 className="text-sm font-semibold text-foreground">
                    Processos que serão criados
                </h3>
                <p
                    className="mt-1 text-2xl font-semibold leading-none text-foreground"
                    data-testid="summary-process-count"
                >
                    {activeGroups.length} {processWord(activeGroups.length)}
                </p>
                <p
                    className="mt-1 text-xs text-muted-foreground dark:text-muted-foreground"
                    data-testid="summary-items-count"
                >
                    {includedCount} de {totalBudgetItems} itens incluídos
                </p>
            </div>

            {outCount > 0 && (
                <p
                    className="flex items-center gap-1 text-xs font-medium text-warning-500"
                    data-testid="excluded-items-note"
                >
                    ⚠ {outCount}{' '}
                    {outCount === 1
                        ? 'item não será incluído'
                        : 'itens não serão incluídos'}
                </p>
            )}

            <ol className="space-y-2">
                {activeGroups.map((g, i) => (
                    <li key={g.temporaryId} className="text-sm">
                        <span className="font-medium text-foreground">
                            {i + 1}. {g.title || '(sem título)'}
                        </span>
                        <br />
                        <span className="text-xs text-muted-foreground dark:text-muted-foreground">
                            {g.items.length}{' '}
                            {g.items.length === 1 ? 'item' : 'itens'} •{' '}
                            {g.quantity ?? '?'} un.
                        </span>
                    </li>
                ))}
                {activeGroups.length === 0 && (
                    <li className="text-xs italic text-muted-foreground">
                        Nenhum grupo ativo.
                    </li>
                )}
            </ol>

            {!canSubmit && activeGroups.length > 0 && (
                <p className="text-xs text-muted-foreground" role="note">
                    Preencha nome e quantidade de cada grupo para habilitar a
                    criação.
                </p>
            )}

            {pendingExcludedConfirm ? (
                <div
                    className="rounded-md border border-warning-200 bg-warning-50/70 p-2 text-xs"
                    data-testid="excluded-confirm"
                >
                    {outCount > 0
                        ? `${outCount} item(ns) ficarão de fora. `
                        : ''}
                    Confirmar a criação agora?
                    <div className="mt-2 flex gap-2">
                        <Button
                            variant={ButtonVariant.secondary}
                            onClick={onCancelExcluded}
                        >
                            Voltar
                        </Button>
                        <Button
                            data-testid="confirm-excluded"
                            variant={ButtonVariant.primary}
                            disabled={creating || !canSubmit}
                            onClick={onConfirmExcluded}
                        >
                            Confirmar criação
                        </Button>
                    </div>
                </div>
            ) : (
                <Button
                    data-testid="create-batch"
                    className="w-full"
                    variant={ButtonVariant.primary}
                    disabled={creating || !canSubmit}
                    onClick={onCreateClick}
                >
                    {creating
                        ? 'Criando…'
                        : createProcessesLabel(activeGroups.length)}
                </Button>
            )}
        </div>
    )
}
